import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [footer, setFooter] = useState({ email: "", phone: "" });

  useEffect(() => {
    api.get("/footer").then((r) => setFooter({ email: r.data.email || "", phone: r.data.phone || "" })).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. I will reply within 48 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send your message. Try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="px-6 md:px-12 lg:px-24 py-32 md:py-48" data-testid="contact-section">
      <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-12">(05) — Contact</p>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
        <div className="md:col-span-7">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[8vw] leading-[0.9]"
          >
            Let&apos;s
            <br />
            <span className="font-serif-italic font-light normal-case lowercase text-[#FF3E00]">build</span> something
            <br />
            real.
          </motion.h2>
        </div>
        <div className="md:col-span-4 md:col-start-9 flex flex-col justify-end gap-6">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Email</p>
            <a href={`mailto:${footer.email}`} className="font-display text-xl link-line" data-testid="email-link">
              {footer.email || "—"}
            </a>
          </div>
          <div>
            <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-2">Phone</p>
            <p className="font-display text-xl">{footer.phone || "—"}</p>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2 max-w-5xl" data-testid="contact-form">
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Your name</label>
          <input
            type="text"
            value={form.name}
            onChange={onChange("name")}
            className="input-bare"
            placeholder="e.g. Alex Morgan"
            data-testid="contact-name-input"
          />
        </div>
        <div>
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Email</label>
          <input
            type="email"
            value={form.email}
            onChange={onChange("email")}
            className="input-bare"
            placeholder="you@company.com"
            data-testid="contact-email-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Subject</label>
          <input
            type="text"
            value={form.subject}
            onChange={onChange("subject")}
            className="input-bare"
            placeholder="What are we making?"
            data-testid="contact-subject-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Tell me more</label>
          <textarea
            value={form.message}
            onChange={onChange("message")}
            rows={4}
            className="input-bare resize-none"
            placeholder="A few sentences about timeline, budget, vibe…"
            data-testid="contact-message-input"
          />
        </div>
        <div className="md:col-span-2 mt-10">
          <button
            type="submit"
            disabled={sending}
            className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl lg:text-[7vw] leading-[0.9] hover:text-[#FF3E00] transition-colors duration-500 disabled:opacity-40"
            data-testid="contact-submit-button"
          >
            {sending ? "Sending…" : "Send it →"}
          </button>
        </div>
      </form>
    </section>
  );
}
